'use client';

import Image from 'next/image';
import { Link } from '@/i18n/routing';
import { useTranslations } from 'next-intl';
import { Home, ChevronRight, Thermometer, Calendar, Wind, Anchor } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import UniqueVisitsIndicator from '@/components/UniqueVisitsIndicator';

interface RegionDetailHeaderProps {
  regionName: string;
  regionId: string;
  description: string;
  imageUrl: string | null;
  imageAlt?: string;
  regionSlug?: string;
  imageAuthorName?: string;
  imageAuthorProfile?: string;
  /** Unique visit count from backend – shown next to like button */
  uniqueVisits?: number | null;
  stats?: {
    averageTemp?: string;
    bestSeason?: string;
    windConditions?: string;
  };
  popularPorts?: Array<{ name: string; country: string }>;
}

export default function RegionDetailHeader({
  regionName,
  description,
  imageUrl,
  imageAlt,
  regionSlug,
  imageAuthorName,
  imageAuthorProfile,
  uniqueVisits,
  stats,
  popularPorts,
}: RegionDetailHeaderProps) {
  const t = useTranslations('regionPage');

  return (
    <section className="bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-950 py-8 md:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8 flex items-center gap-2 text-sm text-slate-400 dark:text-slate-500" aria-label="Breadcrumb">
          <Link href="/" className="hover:text-slate-500 dark:hover:text-slate-400 transition-colors">
            <Home size={18} />
          </Link>
          <ChevronRight size={18} />
          <Link href="/cruises" className="hover:text-slate-500 dark:hover:text-slate-400 transition-colors">
            {t('regions')}
          </Link>
          {regionSlug && (
            <>
              <ChevronRight size={18} />
              <span className="text-slate-500 dark:text-slate-400 font-medium">{regionName}</span>
            </>
          )}
        </nav>

        {/* Hero Card */}
        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-none dark:shadow-[0_0_15px_rgba(0,0,0,0.5)] border border-slate-200 dark:border-slate-800 p-6 md:p-12 max-w-7xl mx-auto">
          <div className={`grid gap-10 items-center ${imageUrl ? 'grid-cols-1 lg:grid-cols-12' : 'grid-cols-1'}`}>

            {/* Left Column: Text Content */}
            <div className={imageUrl ? 'lg:col-span-7 order-2 lg:order-1' : ''}>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white flex-1 min-w-0">
                  {regionName}
                </h1>
                <UniqueVisitsIndicator variant="inline" uniqueVisits={uniqueVisits} />
              </div>

              {description && description.trim() ? (
                <div className="prose prose-slate dark:prose-invert max-w-none prose-p:leading-relaxed prose-p:text-slate-600 dark:prose-p:text-slate-300 prose-p:text-sm md:prose-p:text-base mb-8">
                  <ReactMarkdown>{description}</ReactMarkdown>
                </div>
              ) : null}

              {/* Popular Ports Section */}
              {popularPorts && popularPorts.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-200 mb-3 flex items-center gap-2">
                    <Anchor size={16} className="text-blue-500" />
                    {t('popularStartingPointsIn', { region: regionName })}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {popularPorts.map((port, index) => {
                      const displayText = `${port.name}, ${port.country}`;
                      const queryValue = `${port.name},${port.country}`;
                      return (
                        <Link
                          key={`${port.name}-${port.country}-${index}`}
                          href={{
                            pathname: '/cruises',
                            query: { embarkation_place: queryValue }
                          }}
                          className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-full text-xs font-medium hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
                        >
                          {displayText}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {stats && (
                <div className="flex flex-wrap gap-6 pt-6 border-t border-slate-100 dark:border-slate-800">
                  {stats.averageTemp && (
                    <div className="flex items-center gap-2">
                      <Thermometer size={20} className="text-blue-600 dark:text-blue-400" />
                      <div>
                        <span className="text-slate-500 dark:text-slate-400 block text-xs uppercase tracking-wider">
                          {t('temperature')}
                        </span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                          {stats.averageTemp}
                        </span>
                      </div>
                    </div>
                  )}
                  {stats.bestSeason && (
                    <div className="flex items-center gap-2">
                      <Calendar size={20} className="text-blue-600 dark:text-blue-400" />
                      <div>
                        <span className="text-slate-500 dark:text-slate-400 block text-xs uppercase tracking-wider">
                          {t('season')}
                        </span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                          {stats.bestSeason}
                        </span>
                      </div>
                    </div>
                  )}
                  {stats.windConditions && (
                    <div className="flex items-center gap-2">
                      <Wind size={20} className="text-blue-600 dark:text-blue-400" />
                      <div>
                        <span className="text-slate-500 dark:text-slate-400 block text-xs uppercase tracking-wider">
                          {t('wind')}
                        </span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                          {stats.windConditions}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Column: Image */}
            {imageUrl && (
              <div className="lg:col-span-5 order-1 lg:order-2 space-y-2">
                <div className="relative aspect-video w-full overflow-hidden rounded-2xl shadow-inner bg-slate-100 dark:bg-slate-800">
                  <Image
                    src={imageUrl}
                    alt={imageAlt || regionName}
                    fill
                    className="object-cover"
                    priority
                    unoptimized={true}
                  />
                </div>
                {imageAuthorName && (
                  <div className="mt-2 text-[10px] text-slate-400 dark:text-slate-500 text-right">
                    {t('photo')}:{' '}
                    {imageAuthorProfile ? (
                      <a
                        href={imageAuthorProfile}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 underline transition-colors"
                      >
                        {imageAuthorName}
                      </a>
                    ) : (
                      <span className="text-slate-500 dark:text-slate-400">{imageAuthorName}</span>
                    )}{' '}
                    {t('onUnsplash')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
